import axios from "axios";
import store from "../store";
import router from "../router";

/**
 * HTTP Client with configurable retry mechanism
 * - Supports GET, POST, PUT, DELETE methods
 * - Optional retry for failed requests (4xx, 5xx) after 10 seconds
 * - Maximum 3 retry attempts when enabled
 */
class HttpClient {
  constructor(options = {}) {
    this.enableRetry = options.enableRetry || false;
    this.maxRetries = options.maxRetries || 3;
    this.retryDelay = options.retryDelay || 10000;

    this.instance = axios.create({
      baseURL: process.env.VUE_APP_API_URL || "",
      timeout: 30000,
      headers: {
        "Content-Type": "application/json",
      },
    });

    // Add request interceptor to add auth token
    this.instance.interceptors.request.use((config) => {
      const token = store.getters["user/userToken"];
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });

    // Add response interceptor to handle 401 Unauthorized errors
    this.instance.interceptors.response.use(
      (response) => response,
      (error) => {
        //  "error": "Invalid credentials"
        if (
          error.response &&
          error.response.status === 401 &&
          error.response.data.error === "Invalid token"
        ) {
          console.log(
            "Unauthorized access detected. Redirecting to login page..."
          );
          // Redirect to login page
          router.push("/pages/authentication/login");
        }
        return Promise.reject(error);
      }
    );
  }

  /**
   * Make HTTP request with retry mechanism
   * @param {string} method - HTTP method (GET, POST, PUT, DELETE)
   * @param {string} url - API endpoint
   * @param {Object} data - Request payload (for POST, PUT)
   * @param {Object} params - URL parameters (for GET)
   * @param {Function} callback - Success callback function
   * @param {number} retryCount - Current retry count (internal use)
   * @returns {Promise} - Promise with HTTP response
   */
  async request(
    method,
    url,
    data = null,
    params = null,
    callback = null,
    retryCount = 0
  ) {
    try {
      const config = {
        method,
        url,
        params: method === "GET" ? data : params,
        data: ["POST", "PUT"].includes(method) ? data : null,
      };

      const response = await this.instance(config);

      // If request is successful, call the callback if provided
      if (callback && typeof callback === "function") {
        callback(response);
      }

      return response;
    } catch (error) {
      // Handle error and retry logic
      const status = error.response ? error.response.status : null;

      // If retry is enabled, status is 4xx or 5xx, and we haven't reached max retries
      if (
        this.enableRetry &&
        status &&
        status >= 400 &&
        retryCount < this.maxRetries
      ) {
        console.log(
          `Request failed with status ${status}. Retrying in ${
            this.retryDelay / 1000
          } seconds... (${retryCount + 1}/${this.maxRetries})`
        );

        // Wait before retry
        return new Promise((resolve) => {
          setTimeout(() => {
            resolve(
              this.request(method, url, data, params, callback, retryCount + 1)
            );
          }, this.retryDelay);
        });
      }

      // If retry disabled, max retries reached, or other error, throw the error
      throw error;
    }
  }

  /**
   * HTTP GET request
   * @param {string} url - API endpoint
   * @param {Object} params - URL parameters
   * @param {Function} callback - Success callback function
   * @returns {Promise} - Promise with HTTP response
   */
  get(url, params = null, callback = null) {
    return this.request("GET", url, params, null, callback);
  }

  /**
   * HTTP POST request
   * @param {string} url - API endpoint
   * @param {Object} data - Request payload
   * @param {Function} callback - Success callback function
   * @returns {Promise} - Promise with HTTP response
   */
  post(url, data = null, callback = null) {
    return this.request("POST", url, data, null, callback);
  }

  /**
   * HTTP PUT request
   * @param {string} url - API endpoint
   * @param {Object} data - Request payload
   * @param {Function} callback - Success callback function
   * @returns {Promise} - Promise with HTTP response
   */
  put(url, data = null, callback = null) {
    return this.request("PUT", url, data, null, callback);
  }

  /**
   * HTTP DELETE request
   * @param {string} url - API endpoint
   * @param {Object} params - URL parameters
   * @param {Function} callback - Success callback function
   * @returns {Promise} - Promise with HTTP response
   */
  delete(url, params = null, callback = null) {
    return this.request("DELETE", url, params, null, callback);
  }

  delete_json(url, data = null, callback = null) {
    console.log("delete_json", url, data);
    return this.request("DELETE", url, data, null, callback);
  }
}

// Create and export a singleton instance with retry disabled by default
const httpClient = new HttpClient({ enableRetry: false });
export default httpClient;

// Export the class for custom instances
export { HttpClient };
